import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { PencilSimple, ArrowRight, Plus, MagnifyingGlass, PhoneCall, ChatText, Sparkle, ClockCounterClockwise } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Shell } from "@/components/Shell";
import { CreateBuddyDialog } from "@/components/CreateBuddyDialog";
import { ActivationCredits } from "@/components/ActivationCredits";
import { BuddyList } from "@/components/BuddyList";
import { Onboarding } from "@/components/Onboarding";
import { Switch } from "@/components/ui/switch";
import { useApp } from "@/context/AppContext";
import { contactsSupported, pickContact } from "@/lib/contactPicker";
import api from "@/lib/api";

const Stat = ({ label, value, testid, Icon, onClick, color }) => (
  <button onClick={onClick} data-testid={testid} className="sb-card p-4 flex flex-col items-start active:scale-95 transition-transform text-left">
    <Icon size={22} weight="duotone" color={color} style={{ filter: `drop-shadow(0 0 6px ${color}99)` }} />
    <span className="text-3xl font-heading font-semibold mt-1" style={{ color }}>{value}</span>
    <span className="text-xs font-body text-gray-500 uppercase tracking-wider mt-0.5">{label}</span>
  </button>
);

export default function Dashboard() {
  const { t, lang, simpleMode, setSimpleMode } = useApp();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [evType, setEvType] = useState(null);
  const [events, setEvents] = useState([]);
  const [prefill, setPrefill] = useState(null);

  const startSimpleCreate = async () => {
    let pf = { useDefault: true };
    if (contactsSupported) {
      const picked = await pickContact();
      if (picked && (picked.name || picked.phone)) {
        pf = { useDefault: false, label: picked.name || "", displayName: picked.name || "",
          contacts: [{ id: crypto.randomUUID(), name: picked.name || "", relationship: "", phone: picked.phone || "" }] };
      }
    }
    if (pf.useDefault) {
      try { const pr = (await api.get("/profile")).data; pf.label = [pr.first_name, pr.last_name].filter(Boolean).join(" "); } catch {}
    }
    setPrefill(pf);
    setCreateOpen(true);
  };

  const openEvents = async (type) => {
    setEvType(type);
    setEvents([]);
    try { setEvents((await api.get(`/events?type=${type}`)).data); } catch {}
  };
  const evTitle = { scan: t("eventsScansTitle"), call: t("eventsCallsTitle"), message: t("eventsMessagesTitle") };

  const load = async () => {
    const res = await api.get("/dashboard");
    setData(res.data);
  };
  useEffect(() => { load(); }, []);

  if (!data) return <Shell home><div className="pt-20 text-center font-heading text-gray-500">…</div></Shell>;

  if (simpleMode) return (
    <Shell home>
      <div className="flex flex-col items-center justify-center min-h-[70vh] text-center" data-testid="simple-home">
        <p className="font-body text-gray-500 mb-8">{t("hello")}, {data.first_name || ""}! 👋</p>
        <button data-testid="simple-create-btn" onClick={startSimpleCreate}
          className="w-56 h-56 rounded-full border-4 border-gray-900 text-white font-heading text-2xl font-semibold shadow-xl active:scale-95 transition-transform flex items-center justify-center p-6"
          style={{ background: "var(--app-primary)" }}>
          {t("createNewBuddy")}
        </button>
        <div data-testid="simple-mode-toggle-row" className="mt-12 flex items-center gap-4 rounded-full border-2 border-gray-800 bg-white px-6 py-4 shadow-[3px_3px_0_0_rgba(31,41,55,1)]">
          <span className="font-heading font-semibold text-lg text-gray-800">{t("simpleModeLabel")}</span>
          <Switch data-testid="simple-to-full" checked={simpleMode} onCheckedChange={setSimpleMode} className="scale-[1.6] ml-1" />
        </div>
        <p className="mt-3 font-body text-sm text-gray-400">{t("toFullView")}</p>
      </div>
      <CreateBuddyDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={load} prefill={prefill} />
    </Shell>
  );

  return (
    <Shell home>
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        <h2 className="text-lg font-heading font-semibold mb-3">{t("overview")}</h2>
        <div className="grid grid-cols-3 gap-3">
          <Stat label={t("totalScans")} value={data.total_scans} testid="stat-scans" Icon={MagnifyingGlass} color="#14b8a6" onClick={() => openEvents("scan")} />
          <Stat label={t("totalCalls")} value={data.total_calls} testid="stat-calls" Icon={PhoneCall} color="#3b82f6" onClick={() => openEvents("call")} />
          <Stat label={t("totalMessages")} value={data.total_messages} testid="stat-messages" Icon={ChatText} color="#a855f7" onClick={() => openEvents("message")} />
        </div>

        <h1 className="text-3xl font-heading font-semibold text-gray-900 mt-7" data-testid="dashboard-greeting">
          {t("hello")}, {data.first_name || ""}! 👋
        </h1>
        <p className="font-body text-gray-600 mt-1">{t("welcomeBack")}</p>

        <div className="sb-card p-4 mt-5 flex items-center justify-between" data-testid="dashboard-simple-card">
          <div className="flex items-center gap-2">
            <Sparkle size={22} weight="duotone" style={{ color: "var(--app-strong)" }} />
            <div>
              <span className="font-body font-medium">{t("simpleModeLabel")}</span>
              <p className="font-body text-xs text-gray-500 mt-0.5">{t("simpleModeHint")}</p>
            </div>
          </div>
          <Switch data-testid="dashboard-simple-toggle" checked={simpleMode} onCheckedChange={setSimpleMode} />
        </div>

        <div className="sb-card p-4 mt-5 flex items-center justify-between">
          <div>
            <p className="text-xs font-body text-gray-500 uppercase tracking-wider">{t("activeTags")}</p>
            <p className="text-2xl font-heading font-semibold text-gray-900" data-testid="active-tags-count">{data.active_tags} / {data.total_tags}</p>
          </div>
          <div className="text-right">
            <p className="text-xs font-body text-gray-500 uppercase tracking-wider">{t("blockedTags")}</p>
            <p className="text-2xl font-heading font-semibold" style={{ color: "#6B7280" }} data-testid="blocked-tags-count">{data.blocked_tags}</p>
          </div>
        </div>

        <ActivationCredits />

        <h2 className="text-lg font-heading font-semibold mt-7 mb-3">{t("quickActions")}</h2>
        <div className="space-y-3">
          <button data-testid="quick-edit-profile" onClick={() => navigate("/profile")}
            className="sb-btn w-full py-4 px-5 bg-white flex items-center justify-between text-gray-800">
            <span className="flex items-center gap-3 font-medium"><PencilSimple size={22} weight="bold" style={{ color: "var(--app-primary)" }} />{t("editProfile")}</span>
            <ArrowRight size={20} />
          </button>
          <button data-testid="quick-inbox" onClick={() => navigate("/inbox")}
            className="sb-btn w-full py-4 px-5 bg-white flex items-center justify-between text-gray-800">
            <span className="flex items-center gap-3 font-medium"><ChatText size={22} weight="bold" style={{ color: "var(--app-primary)" }} />{t("inboxTitle")}</span>
            <ArrowRight size={20} />
          </button>
          <button data-testid="quick-activity" onClick={() => navigate("/activity")}
            className="sb-btn w-full py-4 px-5 bg-white flex items-center justify-between text-gray-800">
            <span className="flex items-center gap-3 font-medium"><ClockCounterClockwise size={22} weight="bold" style={{ color: "var(--app-primary)" }} />{t("activityTitle")}</span>
            <ArrowRight size={20} />
          </button>
        </div>
      </motion.div>

      <CreateBuddyDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={load} prefill={prefill} />
      <Onboarding />

      <Dialog open={!!evType} onOpenChange={(o) => !o && setEvType(null)}>
        <DialogContent data-testid="events-modal" className="max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{evType ? evTitle[evType] : ""}</DialogTitle>
            <DialogDescription className="sr-only">Ereignisliste</DialogDescription>
          </DialogHeader>
          {events.length === 0 ? (
            <p className="font-body text-gray-500 text-sm">{t("noEventsYet")}</p>
          ) : (
            <div className="space-y-2">
              {events.map((ev) => (
                <div key={ev.id} className="sb-card p-3" data-testid="event-item">
                  <div className="flex items-center justify-between">
                    <p className="font-heading font-semibold text-gray-900 text-sm">{ev.tag_label || t("unnamed")} <span className="text-gray-400 font-body">/t/{ev.tag_code}</span></p>
                    <p className="text-xs font-body text-gray-400">{new Date(ev.timestamp).toLocaleString(lang === "de" ? "de-AT" : "en-GB")}</p>
                  </div>
                  {evType === "message" && ev.message && (
                    <p className="text-sm font-body text-gray-700 mt-1">"{ev.message}" — {ev.finder_name || "Anonym"}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Shell>
  );
}
